"use client";

import React from "react";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import { Activity, Zap, Server, Globe } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const benchmarkData = [{
  technology: "Yandex YATI",
  latency: 98,
  icon: Zap,
  description: "Analisi semantica profonda"
}, {
  technology: "Google BERT",
  latency: 82,
  icon: Server,
  description: "NLP bidirezionale standard"
}, {
  technology: "Legacy SEO",
  latency: 45,
  icon: Globe,
  description: "Corrispondenza keyword esatta"
}];
const chartConfig = {
  latency: {
    label: "Rilevanza (%)",
    color: "var(--color-primary)"
  }
} satisfies ChartConfig;
export default function PerformanceBenchmarks() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: chartRef,
    isVisible: isChartVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="221783" id="il-dualismo-algoritmico-yati-vs-google" className="bg-background text-foreground py-24 px-4 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-secondary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto max-w-5xl relative z-10">
        <div ref={headerRef} className={`text-center mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-none bg-secondary/10 text-secondary text-sm font-medium mb-6 border border-secondary/20">
            <Zap className="size-4" />
            <span>Intelligence Algoritmica</span>
          </div>
          <h2 className="font-default text-3xl md:text-5xl font-bold tracking-tighter mb-6">
            Il Dualismo Algoritmico: <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
              YATI vs Google BERT.
            </span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default">
            Mentre Google ottimizza per l'intento globale, Yandex YATI penetra la semantica russa 
            con una precisione neurale superiore. Comprendere questa divergenza è il primo passo 
            per dominare il mercato CSI.
          </p>
        </div>

        <div ref={chartRef} className={`transition-all duration-1000 delay-200 ease-out ${isChartVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Card className="border-white/10 bg-card/40 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden rounded-none">
            <CardHeader className="border-b border-white/5 pb-6 bg-white/5">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="font-default text-2xl flex items-center gap-2 text-foreground">
                    <Activity className="size-6 text-primary" />
                    Analisi Comparativa YATI
                  </CardTitle>
                  <CardDescription className="text-base mt-2 text-muted-foreground font-default">
                    Indice di comprensione del contesto linguistico complesso. Valori più alti indicano maggiore precisione.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-6 text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <div className="size-3 rounded-none bg-primary shadow-[0_0_12px_rgba(197,160,89,0.6)] animate-pulse" />
                    <span className="text-foreground font-default">Yandex</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="size-3 rounded-none bg-secondary" />
                    <span className="text-muted-foreground font-default">Google</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-8 pb-4 px-2 sm:px-6">
              <ChartContainer config={chartConfig} className="h-[400px] w-full">
                <BarChart data={benchmarkData} margin={{
                top: 20,
                right: 20,
                left: -20,
                bottom: 20
              }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" opacity={0.2} />
                  <XAxis dataKey="technology" tickLine={false} axisLine={false} tick={{
                  fill: "var(--color-muted-foreground)",
                  fontSize: 14,
                  fontWeight: 500
                }} dy={16} />
                  <YAxis tickLine={false} axisLine={false} tick={{
                  fill: "var(--color-muted-foreground)",
                  fontSize: 12
                }} tickFormatter={value => `${value}%`} dx={-10} />
                  <ChartTooltip cursor={{
                  fill: "var(--color-muted)",
                  opacity: 0.1
                }} content={<ChartTooltipContent formatter={(value, name, props) => <div className="flex flex-col gap-1">
                            <span className="text-muted-foreground text-xs font-mono">{props.payload.description}</span>
                            <div className="flex items-center gap-2 font-mono text-base">
                              <span className="text-foreground font-bold">{value}%</span>
                              <span className="text-muted-foreground">precisione</span>
                            </div>
                          </div>} />} />
                  <Bar dataKey="latency" radius={[2, 2, 0, 0]} animationDuration={1500} animationEasing="ease-out">
                    {benchmarkData.map((entry, index) => <Cell key={`cell-${index}`} data-index={index} fill={entry.technology === "Yandex YATI" ? "var(--color-primary)" : "var(--color-secondary)"} className="transition-all duration-300 hover:opacity-80" />)}
                  </Bar>
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}